import { createContext } from 'preact'
import { useContext, useMemo, useState } from 'preact/hooks'
import { lookupsApi } from '@/api'
import type { ApiResponse } from '@/api/client'
import type { LookupsState, OptionDto, LookupOptionDto, TimeZoneDto } from '@/types/lookups'
import { useAuth } from '@/auth/useAuth'

interface LookupsContextValue extends LookupsState {
  getStatusOptions: () => OptionDto[]
  getCampaignStatuses: () => OptionDto[]
  getFileStatuses: () => OptionDto[]
  getWebTypes: () => OptionDto[]
  getOnlineEventTypes: () => OptionDto[]
  getOnlineModeTypes: () => OptionDto[]
  getSpeakerTypes: () => OptionDto[]
  getAuthenticationTypes: () => OptionDto[]
  getCountries: () => OptionDto[]
  
  // Individual lookup getters
  getTouchPointTypes: () => OptionDto[]
  getCampaignMeetingTypes: () => OptionDto[]
  getWebMeetingTypes: () => OptionDto[]
  getWebStatuses: () => OptionDto[]
  getCompanySizeRanges: () => OptionDto[]
  getTimeZones: () => TimeZoneDto[]
  getUtmSources: () => OptionDto[]
  getPlatforms: () => OptionDto[]
  getAssetTypes: () => OptionDto[]
  getAssetStatuses: () => OptionDto[]
}

const LookupsContext = createContext<LookupsContextValue | null>(null)

// NOTE: This file exports a hook + provider; suppress fast-refresh warning for non-component export.
// eslint-disable-next-line react-refresh/only-export-components
export const useLookups = () => {
  const ctx = useContext(LookupsContext)
  if (!ctx) throw new Error('useLookups must be used within a LookupsProvider')
  return ctx
}

export function LookupsProvider({ children }: { children: preact.ComponentChildren }) {
  const { isAuthenticated } = useAuth()
  const TTL_MS = 60000 // 1 minute TTL
  
  const STORAGE_KEY_TOUCHPOINT_TYPES = 'orbitalx-touchpoint-types'
  const STORAGE_KEY_CAMPAIGN_MEETING_TYPES = 'orbitalx-campaign-meeting-types'
  const STORAGE_KEY_WEB_MEETING_TYPES = 'orbitalx-web-meeting-types'
  const STORAGE_KEY_WEB_STATUSES = 'orbitalx-web-statuses'
  const STORAGE_KEY_COMPANY_SIZE_RANGES = 'orbitalx-company-size-ranges'
  const STORAGE_KEY_TIME_ZONES = 'orbitalx-time-zones'
  const STORAGE_KEY_UTM_SOURCES = 'orbitalx-utm-sources'
  const STORAGE_KEY_PLATFORMS = 'orbitalx-platforms'
  const STORAGE_KEY_WEB_TYPES = 'orbitalx-web-types'
  const STORAGE_KEY_AUTHENTICATION_TYPES = 'orbitalx-authentication-types'
  const STORAGE_KEY_CAMPAIGN_STATUSES = 'orbitalx-campaign-statuses'
  const STORAGE_KEY_COMPANY_STATUSES = 'orbitalx-company-statuses'
  const STORAGE_KEY_FILE_STATUSES = 'orbitalx-file-statuses'
  const STORAGE_KEY_ONLINE_EVENT_TYPES = 'orbitalx-online-event-types'
  const STORAGE_KEY_ONLINE_MODE_TYPES = 'orbitalx-online-mode-types'
  const STORAGE_KEY_SPEAKER_TYPES = 'orbitalx-speaker-types'
  const STORAGE_KEY_WORKSPACE_STATUSES = 'orbitalx-workspace-statuses'
  const STORAGE_KEY_COUNTRIES = 'orbitalx-countries'
  const STORAGE_KEY_ASSET_TYPES = 'orbitalx-asset-types'
  const STORAGE_KEY_ASSET_STATUSES = 'orbitalx-asset-statuses'
  
  // Track in-flight requests to prevent duplicate fetches
  const [inFlightRequests] = useState(new Map<string, Promise<void>>())
  
  const [state, setState] = useState<LookupsState>(() => {
    const loadCache = (key: string) => {
      try {
        const raw = localStorage.getItem(key)
        if (raw) {
          const parsed = JSON.parse(raw)
          return { etag: parsed?.etag || null, data: parsed?.data || null, fetchedAt: parsed?.fetchedAt || null }
        }
      } catch (e) {
        if (import.meta.env.DEV) console.debug(`Cache parse failed for ${key}`, e)
      }
      return { etag: null, data: null, fetchedAt: null }
    }

    return {
      isLoading: false,
      error: null,
      touchPointTypes: loadCache(STORAGE_KEY_TOUCHPOINT_TYPES),
      campaignMeetingTypes: loadCache(STORAGE_KEY_CAMPAIGN_MEETING_TYPES),
      webMeetingTypes: loadCache(STORAGE_KEY_WEB_MEETING_TYPES),
      webStatuses: loadCache(STORAGE_KEY_WEB_STATUSES),
      companySizeRanges: loadCache(STORAGE_KEY_COMPANY_SIZE_RANGES),
      timeZones: loadCache(STORAGE_KEY_TIME_ZONES),
      utmSources: loadCache(STORAGE_KEY_UTM_SOURCES),
      platforms: loadCache(STORAGE_KEY_PLATFORMS),
      webTypes: loadCache(STORAGE_KEY_WEB_TYPES),
      authenticationTypes: loadCache(STORAGE_KEY_AUTHENTICATION_TYPES),
      campaignStatuses: loadCache(STORAGE_KEY_CAMPAIGN_STATUSES),
      companyStatuses: loadCache(STORAGE_KEY_COMPANY_STATUSES),
      fileStatuses: loadCache(STORAGE_KEY_FILE_STATUSES),
      onlineEventTypes: loadCache(STORAGE_KEY_ONLINE_EVENT_TYPES),
      onlineModeTypes: loadCache(STORAGE_KEY_ONLINE_MODE_TYPES),
      speakerTypes: loadCache(STORAGE_KEY_SPEAKER_TYPES),
      workspaceStatuses: loadCache(STORAGE_KEY_WORKSPACE_STATUSES),
      countries: loadCache(STORAGE_KEY_COUNTRIES),
      assetTypes: loadCache(STORAGE_KEY_ASSET_TYPES),
      assetStatuses: loadCache(STORAGE_KEY_ASSET_STATUSES)
    }
  })

  const fetchIndividualLookup = async (
    apiCall: (etag?: string) => Promise<ApiResponse<LookupOptionDto[] | TimeZoneDto[]>>,
    stateKey: 'touchPointTypes' | 'campaignMeetingTypes' | 'webMeetingTypes' | 'webStatuses' | 'companySizeRanges' | 'timeZones' | 'utmSources' | 'platforms' | 'webTypes' | 'authenticationTypes' | 'campaignStatuses' | 'companyStatuses' | 'fileStatuses' | 'onlineEventTypes' | 'onlineModeTypes' | 'speakerTypes' | 'workspaceStatuses' | 'countries' | 'assetTypes' | 'assetStatuses',
    storageKey: string
  ) => {
    // Check if already in-flight
    if (inFlightRequests.has(stateKey)) {
      return inFlightRequests.get(stateKey)
    }
    
    // Check TTL - if data was fetched within the last minute, don't refetch
    const cached = state[stateKey]
    if (cached?.fetchedAt && cached.data) {
      const age = Date.now() - cached.fetchedAt
      if (age < TTL_MS) {
        return Promise.resolve()
      }
    }
    
    const promise = (async () => {
      try {
        const res = await apiCall(cached?.etag || undefined)
        
        if (res.status === 304) {
          // Not modified - update fetchedAt timestamp
          const refreshed = { ...cached, fetchedAt: Date.now() }
          setState(prev => ({ ...prev, [stateKey]: refreshed }))
          try {
            localStorage.setItem(storageKey, JSON.stringify(refreshed))
          } catch (e) {
            if (import.meta.env.DEV) console.debug(`Persist ${stateKey} cache failed`, e)
          }
          return
        }
        
        if (res.error) return
        
        const newCached = { etag: res.etag || null, data: res.data || null, fetchedAt: Date.now() }
        setState(prev => ({ ...prev, [stateKey]: newCached }))
        try {
          localStorage.setItem(storageKey, JSON.stringify(newCached))
        } catch (e) {
          if (import.meta.env.DEV) console.debug(`Persist ${stateKey} cache failed`, e)
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.debug(`Failed to fetch ${stateKey}`, e)
        }
      } finally {
        inFlightRequests.delete(stateKey)
      }
    })()
    
    inFlightRequests.set(stateKey, promise)
    return promise
  }

  const value: LookupsContextValue = useMemo(() => ({
    ...state,
    getStatusOptions: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getCompanyStatuses, 'companyStatuses', STORAGE_KEY_COMPANY_STATUSES)
      return (state.companyStatuses?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getCampaignStatuses: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getCampaignStatuses, 'campaignStatuses', STORAGE_KEY_CAMPAIGN_STATUSES)
      return (state.campaignStatuses?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getFileStatuses: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getFileStatuses, 'fileStatuses', STORAGE_KEY_FILE_STATUSES)
      return (state.fileStatuses?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getWebTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getWebTypes, 'webTypes', STORAGE_KEY_WEB_TYPES)
      return (state.webTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getOnlineEventTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getOnlineEventTypes, 'onlineEventTypes', STORAGE_KEY_ONLINE_EVENT_TYPES)
      return (state.onlineEventTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getOnlineModeTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getOnlineModeTypes, 'onlineModeTypes', STORAGE_KEY_ONLINE_MODE_TYPES)
      return (state.onlineModeTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getSpeakerTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getSpeakerTypes, 'speakerTypes', STORAGE_KEY_SPEAKER_TYPES)
      return (state.speakerTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getAuthenticationTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getAuthenticationTypes, 'authenticationTypes', STORAGE_KEY_AUTHENTICATION_TYPES)
      return (state.authenticationTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getCountries: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getCountries, 'countries', STORAGE_KEY_COUNTRIES)
      return (state.countries?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    
    // Convert LookupOptionDto to OptionDto format (Id -> Key, DisplayName -> Description)
    getTouchPointTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getTouchPointTypes, 'touchPointTypes', STORAGE_KEY_TOUCHPOINT_TYPES)
      return (state.touchPointTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getCampaignMeetingTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getCampaignMeetingTypes, 'campaignMeetingTypes', STORAGE_KEY_CAMPAIGN_MEETING_TYPES)
      return (state.campaignMeetingTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getWebMeetingTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getWebMeetingTypes, 'webMeetingTypes', STORAGE_KEY_WEB_MEETING_TYPES)
      return (state.webMeetingTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getWebStatuses: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getWebStatuses, 'webStatuses', STORAGE_KEY_WEB_STATUSES)
      return (state.webStatuses?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getCompanySizeRanges: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getCompanySizeRanges, 'companySizeRanges', STORAGE_KEY_COMPANY_SIZE_RANGES)
      return (state.companySizeRanges?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getTimeZones: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getTimeZones, 'timeZones', STORAGE_KEY_TIME_ZONES)
      return state.timeZones?.data || []
    },
    getUtmSources: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getUtmSources, 'utmSources', STORAGE_KEY_UTM_SOURCES)
      return (state.utmSources?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getPlatforms: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getPlatforms, 'platforms', STORAGE_KEY_PLATFORMS)
      return (state.platforms?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getAssetTypes: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getAssetTypes, 'assetTypes', STORAGE_KEY_ASSET_TYPES)
      return (state.assetTypes?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    },
    getAssetStatuses: () => {
      if (isAuthenticated) fetchIndividualLookup(lookupsApi.getAssetStatuses, 'assetStatuses', STORAGE_KEY_ASSET_STATUSES)
      return (state.assetStatuses?.data || []).map(x => ({ Key: x.Id, Description: x.DisplayName }))
    }
  }), [state, isAuthenticated])

  return (
    <LookupsContext.Provider value={value}>
      {children}
    </LookupsContext.Provider>
  )
}
